import React, { useState, useEffect } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'https://drvivara.cloud/api' });
api.interceptors.request.use(c => { const t = localStorage.getItem('token'); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [message, setMessage] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [reelSub, setReelSub] = useState(null);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const params = {};
      if (search) params.q = search;
      if (roleFilter) params.role = roleFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await api.get('/admin/users/search', { params });
      if (res.data.success) setUsers(res.data.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleAction = async (userId, action, data = {}) => {
    try {
      let res;
      switch (action) {
        case 'approve': res = await api.put(`/admin/approve/user/${userId}`); break;
        case 'reject': res = await api.put(`/admin/reject/user/${userId}`); break;
        case 'suspend': res = await api.put(`/admin/users/${userId}/suspend`); break;
        case 'activate': res = await api.put(`/admin/users/${userId}/activate`); break;
        case 'delete': res = await api.delete(`/admin/users/${userId}`); break;
        case 'hard-delete': res = await api.delete(`/admin/users/${userId}/hard`); break;
        case 'reset-password': res = await api.put(`/admin/users/${userId}/reset-password`, { new_password: data.password }); break;
        default: return;
      }
      if (res.data.success) { setMessage(`${action} successful`); fetchUsers(); }
    } catch (e) { setMessage(`${action} failed`); }
  };

  const viewUser = async (userId) => {
    try {
      const [userRes, reelRes] = await Promise.all([
        api.get(`/admin/users/${userId}`),
        api.get(`/admin/users/${userId}/reel-subscription`).catch(() => ({ data: { success: false } }))
      ]);
      if (userRes.data.success) setSelectedUser(userRes.data.data);
      if (reelRes.data.success) setReelSub(reelRes.data.data);
      else setReelSub(null);
    } catch (e) { setMessage('Failed to fetch user details'); }
  };

  const cancelReelSub = async (userId) => {
    if (!window.confirm('Cancel this user\'s reel upload subscription?')) return;
    try {
      const res = await api.delete(`/admin/users/${userId}/reel-subscription`);
      if (res.data.success) { setMessage('Reel subscription cancelled'); setReelSub(null); }
    } catch (e) { setMessage('Failed to cancel reel subscription'); }
  };

  if (loading) return <div className="p-8 text-center text-gold">Loading...</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-gold mb-6">User Management</h1>
      {message && <p className="text-green-400 mb-4">{message}</p>}

      <div className="flex gap-4 mb-6 flex-wrap">
        <input type="text" placeholder="Search by name, email, phone..." value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-gray-800 text-light border border-gray-600 rounded px-4 py-2 flex-1 min-w-[200px]" />
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
          className="bg-gray-800 text-light border border-gray-600 rounded px-3 py-2">
          <option value="">All Roles</option>
          <option value="patient">Patient</option>
          <option value="doctor">Doctor</option>
          <option value="clinic">Clinic</option>
          <option value="hospital">Hospital</option>
          <option value="retailer">Retailer</option>
          <option value="admin">Admin</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-gray-800 text-light border border-gray-600 rounded px-3 py-2">
          <option value="">All Status</option>
          <option value="approved">Approved</option>
          <option value="pending">Pending</option>
          <option value="suspended">Suspended</option>
        </select>
        <button onClick={fetchUsers} className="bg-gold text-dark px-4 py-2 rounded font-semibold">Search</button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead><tr className="text-gray-400 border-b border-gray-700">
            <th className="py-2 px-3">Name</th><th className="py-2 px-3">Email</th>
            <th className="py-2 px-3">Role</th><th className="py-2 px-3">Status</th>
            <th className="py-2 px-3">Joined</th><th className="py-2 px-3">Actions</th>
          </tr></thead>
          <tbody>
            {users.map(user => (
              <tr key={user._id} className="border-b border-gray-800 hover:bg-gray-800/50">
                <td className="py-2 px-3 text-light">{user.name}</td>
                <td className="py-2 px-3 text-gray-400">{user.email}</td>
                <td className="py-2 px-3">
                  <span className="capitalize px-2 py-0.5 rounded text-xs bg-gray-700">{user.role}</span>
                </td>
                <td className="py-2 px-3">
                  <span className={`px-2 py-0.5 rounded text-xs ${user.is_approved ? 'bg-green-600' : 'bg-yellow-600'}`}>
                    {user.is_approved ? 'Approved' : 'Pending'}
                  </span>
                </td>
                <td className="py-2 px-3 text-gray-400">{new Date(user.createdAt).toLocaleDateString()}</td>
                <td className="py-2 px-3">
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => viewUser(user._id)} className="text-blue-400 hover:text-blue-300 text-xs">View</button>
                    <button onClick={() => handleAction(user._id, 'approve')} className="text-green-400 hover:text-green-300 text-xs">Approve</button>
                    <button onClick={() => handleAction(user._id, 'suspend')} className="text-yellow-400 hover:text-yellow-300 text-xs">Suspend</button>
                    <button onClick={() => handleAction(user._id, 'activate')} className="text-blue-400 hover:text-blue-300 text-xs">Activate</button>
                    <button onClick={() => {
                      const pwd = prompt('Enter new password (min 8 chars):');
                      if (pwd && pwd.length >= 8) handleAction(user._id, 'reset-password', { password: pwd });
                    }} className="text-purple-400 hover:text-purple-300 text-xs">Reset Pwd</button>
                    <button onClick={() => { if (window.confirm('Delete this user?')) handleAction(user._id, 'delete'); }} className="text-red-400 hover:text-red-300 text-xs">Delete</button>
                    <button onClick={() => { if (window.confirm('PERMANENTLY delete this user and ALL their data from the database? This cannot be undone!')) handleAction(user._id, 'hard-delete'); }} className="text-red-600 hover:text-red-500 text-xs font-bold">Delete Forever</button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan="6" className="text-center py-8 text-gray-500">No users found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setSelectedUser(null)}>
          <div className="bg-gray-900 rounded-lg p-6 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-gold mb-4">User Details</h2>
            <div className="space-y-2 text-sm">
              <p><span className="text-gray-400">Name:</span> <span className="text-light">{selectedUser.name}</span></p>
              <p><span className="text-gray-400">Email:</span> <span className="text-light">{selectedUser.email}</span></p>
              <p><span className="text-gray-400">Phone:</span> <span className="text-light">{selectedUser.phone || 'N/A'}</span></p>
              <p><span className="text-gray-400">Role:</span> <span className="text-light capitalize">{selectedUser.role}</span></p>
              <p><span className="text-gray-400">Status:</span>
                <span className={`ml-2 px-2 py-0.5 rounded text-xs ${selectedUser.is_approved ? 'bg-green-600' : 'bg-yellow-600'}`}>
                  {selectedUser.is_approved ? 'Approved' : 'Pending'}
                </span>
              </p>
              <p><span className="text-gray-400">Subscription:</span>
                <span className={`ml-2 px-2 py-0.5 rounded text-xs ${selectedUser.subscription_status === 'active' ? 'bg-green-600' : 'bg-gray-600'}`}>
                  {selectedUser.subscription_status || 'N/A'}
                </span>
              </p>
              <p><span className="text-gray-400">Joined:</span> <span className="text-light">{new Date(selectedUser.createdAt).toLocaleString()}</span></p>
              {selectedUser.current_subscription && (
                <div className="mt-2 p-3 bg-gray-800 rounded">
                  <p className="text-gold font-semibold">Current Subscription</p>
                  <p className="text-gray-400">Plan: {selectedUser.current_subscription.plan_name}</p>
                  <p className="text-gray-400">Ends: {new Date(selectedUser.current_subscription.end_date).toLocaleDateString()}</p>
                </div>
              )}
              {['doctor','hospital','clinic','retailer'].includes(selectedUser.role) && (
                <div className="mt-3 p-3 bg-gray-800 rounded">
                  <p className="text-gold font-semibold mb-1">Reel Upload Subscription</p>
                  {reelSub ? (
                    <>
                      <p className="text-gray-400 text-xs">Plan: {reelSub.plan_name}</p>
                      <p className="text-gray-400 text-xs">Status: <span className="text-green-400">{reelSub.status}</span></p>
                      <p className="text-gray-400 text-xs">Ends: {new Date(reelSub.end_date).toLocaleDateString()}</p>
                      {reelSub.status === 'active' && (
                        <button onClick={() => cancelReelSub(selectedUser._id)}
                          className="mt-2 bg-red-600 text-white text-xs px-3 py-1 rounded hover:bg-red-500">
                          Cancel Reel Subscription
                        </button>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-500 text-xs">No reel subscription</p>
                  )}
                </div>
              )}
            </div>
            <button onClick={() => setSelectedUser(null)} className="mt-4 bg-gray-700 text-light px-4 py-2 rounded w-full">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
